import { useState, useEffect, useCallback } from "react";
import useHookMount from "./useHookMount";

const useCustomerLogin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Define loginCustomer using useCallback to maintain reference stability
    const loginCustomer = useCallback(async (email, password, newsletterOptin = false) => {
        setLoading(true);
        setError(null);

        console.log(`Attempting to log in with email: ${email}, password: ${password}, newsletterOptin: ${newsletterOptin}`);
        try {
            // Create a form element programmatically
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/account/login';
            form.style.display = 'none'; // Hide the form

            // Create form inputs for email, password, and required hidden fields
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'customer[email]';
            emailInput.value = email;
            form.appendChild(emailInput);

            const passwordInput = document.createElement('input');
            passwordInput.type = 'hidden';
            passwordInput.name = 'customer[password]';
            passwordInput.value = password;
            form.appendChild(passwordInput);

            const formTypeInput = document.createElement('input');
            formTypeInput.type = 'hidden';
            formTypeInput.name = 'form_type';
            formTypeInput.value = 'customer_login';
            form.appendChild(formTypeInput);

            const utf8Input = document.createElement('input');
            utf8Input.type = 'hidden';
            utf8Input.name = 'utf8';
            utf8Input.value = '✓';
            form.appendChild(utf8Input);
            
            // Add newsletter signup if enabled
            if (newsletterOptin) {
                const newsletterInput = document.createElement('input');
                newsletterInput.type = 'hidden';
                newsletterInput.name = 'customer[accepts_marketing]';
                newsletterInput.value = '1';
                form.appendChild(newsletterInput);
            }

            // Append the form to the document body
            document.body.appendChild(form);
            
            // Use Shopify's CAPTCHA protection if available
            if (window.Shopify && window.Shopify.captcha) {
                window.Shopify.captcha.protect(form, () => {
                    // Form will be submitted after CAPTCHA validation
                    console.log('Submitting login form with CAPTCHA protection');
                                   form.submit();
                });
            } else {
                // Submit without CAPTCHA if not available
                console.log('Submitting login form without CAPTCHA protection');
                form.submit();
            }

            setSuccess(true);
            

        } catch (err) {
            console.error('Login error:', err);
            setError('Login failed. Please try again.');
            setLoading(false);
        }
    }, []); // Empty dependency array to ensure stable reference

    // Create the hook instance with all methods that need to be registered
    const hookInstance = {
        loginCustomer,
        loading,
        error,
        success
    };

    // Use our custom useHookMount utility to ensure proper registration
    useHookMount('customer_login', hookInstance);

    // Add event listener for form submissions
    useEffect(() => {
        console.log("useCustomerLogin hook mounted - adding event listener");
        
        // Handle form submit events directly related to login
        const handleFormSubmit = (event) => {
            const { formType, formFields } = event.detail;
            
            console.log(`useCustomerLogin received form event: ${formType}`);
            
            // Check if this is a login form
            if (formType === "customer_login" || formType === "login") {
                // Extract email and password from form fields
                const email = formFields['customer[email]'] || formFields.email;
                const password = formFields['customer[password]'] || formFields.password;
                const newsletterOptin = formFields['customer[accepts_marketing]'] === '1' || 
                                        formFields.accepts_marketing === '1' || 
                                        formFields.newsletter === 'on';
                
                if (email && password) {
                    console.log(`useCustomerLogin handling login for: ${email}`);
                    loginCustomer(email, password, newsletterOptin);
                }
            }
        };

        // Add event listener when component mounts
        window.addEventListener('formSubmitted', handleFormSubmit);
        
        // Remove event listener when component unmounts
        return () => {
            console.log("useCustomerLogin hook unmounting - removing event listener");
            window.removeEventListener('formSubmitted', handleFormSubmit);
        };
    }, [loginCustomer]); // Include loginCustomer in the dependencies since it's now stable

    return {
        loginCustomer,
        loading,
        error,
        success,
    };
};

export default useCustomerLogin;
